import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  Ban,
  Check,
  CircleCheck,
  Clock,
  FileText,
  Inbox,
  Loader2,
  MessageCircleQuestion,
  Send,
  Sparkles,
  TriangleAlert,
  User,
  UserCheck,
  Workflow,
  Upload,
  Cog,
} from 'lucide-react';
import { STATUS_META, deadlineTone, type PeriodStatus, type StatusTone, type BoxCode } from '@btw/shared';
import { useT } from '../i18n';

const STATUS_ICONS = {
  inbox: Inbox,
  'message-circle-question': MessageCircleQuestion,
  'file-text': FileText,
  loader: Loader2,
  'triangle-alert': TriangleAlert,
  'circle-check': CircleCheck,
  send: Send,
  check: Check,
  ban: Ban,
} as const;

export function Badge({ tone = 'neutral', icon, children, small, className }: { tone?: StatusTone | 'brand'; icon?: ReactNode; children: ReactNode; small?: boolean; className?: string }) {
  return (
    <span className={['badge', `badge--${tone}`, small && 'badge--sm', className].filter(Boolean).join(' ')}>
      {icon}
      {children}
    </span>
  );
}

/** Status badge = icon + text + colour, never colour alone (§8.2). Overdue is an extra danger tag "Te laat". */
export function StatusBadge({
  status,
  openQuestions = 0,
  differences = 0,
  overdueDays,
}: {
  status: PeriodStatus;
  openQuestions?: number;
  differences?: number;
  overdueDays?: number | null;
}) {
  const t = useT();
  const meta = STATUS_META[status];
  const Icon = STATUS_ICONS[meta.icon];
  const n = status === 'questions_open' ? openQuestions : status === 'review_differences' ? differences : 0;
  const label = t(`status.${status}`) + (n > 0 ? ` (${n})` : '');
  return (
    <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
      <Badge tone={meta.tone} icon={<Icon size={14} aria-hidden="true" className={meta.icon === 'loader' ? 'spin' : undefined} />}>
        {label}
      </Badge>
      {overdueDays != null && overdueDays > 0 && (
        <Badge tone="danger" small>
          {t('status.late')}
        </Badge>
      )}
    </span>
  );
}

/** "voorlopig" — hatched background plus text (§8.3, §11.3). */
export function ProvisionalBadge({ final = false }: { final?: boolean }) {
  const t = useT();
  if (final)
    return (
      <Badge tone="success" small icon={<Check size={12} aria-hidden="true" />}>
        {t('amount.final')}
      </Badge>
    );
  return (
    <span className="badge badge--sm badge--provisional" title={t('amount.provisionalHint')}>
      {t('amount.provisional')}
    </span>
  );
}

/** Countdown badge with thresholds: ≥15 neutral, 4–14 warning, ≤3 or overdue danger (§5.4). */
export function DeadlineBadge({ daysLeft }: { daysLeft: number }) {
  const t = useT();
  const tone = deadlineTone(daysLeft);
  const text = daysLeft < 0 ? t('deadline.late', { n: -daysLeft }) : daysLeft === 0 ? t('deadline.today') : t('deadline.left', { n: daysLeft });
  return (
    <Badge tone={tone} icon={<Clock size={14} aria-hidden="true" />}>
      {text}
    </Badge>
  );
}

type SourceKind = 'ai' | 'rule' | 'user' | 'advisor' | 'system' | 'import';
const SOURCE_ICON: Record<SourceKind, typeof Sparkles> = {
  ai: Sparkles,
  rule: Workflow,
  user: User,
  advisor: UserCheck,
  system: Cog,
  import: Upload,
};
const SOURCE_CLASS: Record<SourceKind, string> = {
  ai: 'ai',
  rule: 'rule',
  user: 'user',
  advisor: 'advisor',
  system: 'rule',
  import: 'rule',
};

/**
 * Who produced this: AI / Regel / Jij / Adviseur (FR-26). The AI badge is a small button: hover, focus or a tap
 * shows "Voorgesteld door AI — controleer het zelf", so touch users see it too (DC-06).
 */
export function SourceBadge({ source, model, prefix }: { source: SourceKind; model?: string | null; prefix?: string }) {
  const t = useT();
  const Icon = SOURCE_ICON[source];
  const label = t(`source.${source}`);
  const tip = source === 'ai' ? t('source.aiTip') + (model ? ` · ${model}` : '') : undefined;
  const content = (
    <>
      <Icon aria-hidden="true" />
      {prefix ? `${prefix} · ` : ''}
      {label}
    </>
  );
  if (!tip) return <span className={`source-badge source-badge--${SOURCE_CLASS[source]}`}>{content}</span>;
  return (
    <TipButton className={`source-badge source-badge--${SOURCE_CLASS[source]} source-badge--button`} tip={tip}>
      {content}
    </TipButton>
  );
}

/**
 * A button that shows a short explanation on hover, keyboard focus and tap (toggle). The bubble is not rendered
 * while hidden (no invisible box that widens the page on phones) and is shifted to stay inside the viewport.
 */
export function TipButton({ tip, className, children }: { tip: string; className?: string; children: ReactNode }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [shift, setShift] = useState(0);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const shown = open || hover;

  useLayoutEffect(() => {
    if (!shown || !tipRef.current) return;
    const r = tipRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const margin = 8;
    const left = r.left - shift;
    const right = r.right - shift;
    setShift(left < margin ? margin - left : right > vw - margin ? vw - margin - right : 0);
  }, [shown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className="tooltip-wrap" ref={wrapRef} data-open={shown || undefined} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button
        type="button"
        className={className}
        aria-describedby={id}
        aria-expanded={open}
        onClick={(e) => {
          // Badges sit inside clickable rows and question options: the tap is for the explanation only.
          e.stopPropagation();
          e.preventDefault();
          setOpen((o) => !o);
        }}
        // Keyboard focus shows it; a mouse/touch press focuses too, but there the click toggles.
        onFocus={(e) => e.currentTarget.matches(':focus-visible') && setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </button>
      {/* Always in the DOM for aria-describedby; display:none until shown (DC-06). */}
      <span className="tooltip" role="tooltip" id={id} ref={tipRef} style={shown && shift ? { marginLeft: shift } : undefined}>
        {tip}
      </span>
    </span>
  );
}

export function BoxTag({ code }: { code: BoxCode | string }) {
  return <span className="box-tag">{code}</span>;
}

export function CountBadge({ n, tone, label }: { n: number; tone?: 'warning' | 'info'; label?: string }) {
  if (n <= 0) return null;
  return (
    <span className={['count-badge', tone && `count-badge--${tone}`].filter(Boolean).join(' ')} aria-label={label}>
      {n > 99 ? '99+' : n}
    </span>
  );
}
